import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layouts/MainLayout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { supabase } from "@/integrations/supabase/client";
import { Profile, UserRole } from "@/models/user";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Settings = () => {
  const { user, refreshUser, isAdmin } = useAuth();
  const [fullName, setFullName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<Profile[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<UserRole>("seller");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user?.profile?.full_name) {
      setFullName(user.profile.full_name);
    }
    if (isAdmin) {
      loadUsers();
    }
  }, [user?.profile?.full_name, isAdmin]);

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    setErrorMessage(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('full_name');
      
      if (error) throw error;
      
      // Cast the role field from string to UserRole type
      const typedData = data?.map(user => ({
        ...user,
        role: user.role as UserRole // This ensures the role is typed correctly
      })) || [];
      
      setUsers(typedData);
      console.log("Loaded users:", typedData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('No se pudieron cargar los usuarios');
      setErrorMessage('Error al cargar usuarios: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMessage(null);
    const userId = user?.id;
    if (!userId) {
      toast.error("No hay usuario autenticado.");
      setIsSaving(false);
      setErrorMessage("No hay usuario autenticado");
      return;
    }
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ full_name: fullName })
        .eq("id", userId);

      if (error) {
        console.error("Error guardando el nombre:", error);
        toast.error("Error al guardar el nombre.");
        setErrorMessage("Error al guardar el nombre: " + error.message);
      } else {
        toast.success("Nombre guardado con éxito.");
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      console.error("Error guardando el nombre:", error);
      toast.error("Error al guardar el nombre.");
      setErrorMessage("Error al guardar el nombre: " + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUserId) {
      toast.error("Por favor selecciona un usuario");
      return;
    }

    setIsUpdatingRole(true);
    setErrorMessage(null);
    try {
      console.log("Updating user role:", {
        userId: selectedUserId,
        role: selectedRole
      });

      const { error } = await supabase
        .from('profiles')
        .update({ role: selectedRole })
        .eq('id', selectedUserId);

      if (error) throw error;
      
      toast.success(`Rol actualizado a ${selectedRole} exitosamente`);
      await loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar el rol');
      setErrorMessage('Error al actualizar rol: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleCreateTestUser = async (role: UserRole) => {
    setIsUpdatingRole(true);
    setErrorMessage(null);
    
    try {
      const email = role === 'admin' ? 'admin@outdoorads.com' : 'vendedor@outdoorads.com';
      const password = role === 'admin' ? 'admin123' : 'vendor123';
      
      // Register the user with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (authError) throw authError;
      
      if (authData.user) {
        // Update the profile with the appropriate role
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            role,
            full_name: role === 'admin' ? 'Administrador' : 'Vendedor'
          })
          .eq('id', authData.user.id);
        
        if (profileError) throw profileError;
        
        toast.success(`Usuario de prueba ${role} creado exitosamente`);
        await loadUsers();
      }
    } catch (error) {
      console.error(`Error creating test ${role}:`, error);
      toast.error(`Error al crear usuario de prueba ${role}`);
      setErrorMessage(`Error al crear usuario de prueba: ${(error instanceof Error ? error.message : String(error))}`);
    } finally {
      setIsUpdatingRole(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-lg mx-auto">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
          <p className="text-muted-foreground">
            Aquí puedes personalizar tu información.
          </p>
        </div>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              {user?.profile?.role === 'mantenimiento' ? 'Nombre del Encargado de Mantenimiento' : 'Nombre del Vendedor'}
            </CardTitle>
            <CardDescription>
              {user?.profile?.role === 'mantenimiento' 
                ? 'Este nombre aparecerá en los reportes de mantenimiento.'
                : 'Este nombre aparecerá como "Vendedor" cuando agregues un anuncio.'}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSave}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre completo</Label>
                <Input
                  id="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Tu nombre"
                  autoComplete="off"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                type="submit" 
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar nombre
              </Button>
            </CardFooter>
          </form>
        </Card>

        {isAdmin && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Roles</CardTitle>
                <CardDescription>
                  Asigna roles a los usuarios del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user">Usuario</Label>
                  <Select
                    value={selectedUserId}
                    onValueChange={setSelectedUserId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecciona un usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || user.email || 'Usuario sin nombre'}
                          {user.role === 'admin' ? ' (Admin)' : user.role === 'contabilidad' ? ' (Contabilidad)' : user.role === 'mantenimiento' ? ' (Enc Mantenimiento)' : ' (Vendedor)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Rol</Label>
                  <Select
                    value={selectedRole}
                    onValueChange={(value: UserRole) => setSelectedRole(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecciona un rol" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="seller">Vendedor</SelectItem>
                      <SelectItem value="contabilidad">Contabilidad</SelectItem>
                      <SelectItem value="mantenimiento">Enc Mantenimiento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleUpdateRole}
                  disabled={isUpdatingRole || !selectedUserId}
                  className="flex items-center gap-2"
                >
                  {isUpdatingRole && <Loader2 className="h-4 w-4 animate-spin" />}
                  Actualizar Rol
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Crear Usuarios de Prueba</CardTitle>
                <CardDescription>
                  Crea cuentas de prueba predeterminadas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <p className="mb-2">Cuentas de prueba disponibles:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Admin:</strong> admin@outdoorads.com / admin123</li>
                    <li><strong>Vendedor:</strong> vendedor@outdoorads.com / vendor123</li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex space-x-4">
                <Button 
                  onClick={() => handleCreateTestUser('admin')}
                  disabled={isUpdatingRole}
                  className="flex items-center gap-2"
                  variant="outline"
                >
                  {isUpdatingRole && <Loader2 className="h-4 w-4 animate-spin" />}
                  Crear Administrador
                </Button>
                <Button 
                  onClick={() => handleCreateTestUser('seller')}
                  disabled={isUpdatingRole}
                  className="flex items-center gap-2"
                  variant="outline"
                >
                  {isUpdatingRole && <Loader2 className="h-4 w-4 animate-spin" />}
                  Crear Vendedor
                </Button>
              </CardFooter>
            </Card>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Settings;
